import { connect } from 'react-redux';
import { Lens, fromTraversable, Prism } from 'monocle-ts';
import { Traversable } from 'fp-ts/lib/Array';
import { get, isEmpty } from 'lodash';
import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { WorklistValueProps, WorklistDispatchProps } from 'src/pages/Worklist/Worklist.types';
import { WorklistOwnProps } from 'configuration-schemas/Worklist';
import {
  requestWorklistConfig,
  receiveWorklistConfig,
  receiveWorklistError,
  updateSelectedItem,
  updateSelectedLevel,
  updateWorklistFlowStatus,
  updateWorklistSearch,
  WorklistSlice,
  maybeToggleSelection,
} from 'src/pages/Worklist/Worklist.slice';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { worklistLens, subheaderLens } from 'src/services/lenses/lenses';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import Worklist from 'src/pages/Worklist/Worklist';
import { updateSearch } from 'src/components/Subheader/Subheader.slice';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { update } from 'src/services/lenses/Lenses.actions';
import { getProcessedData } from './Worklist.selectors';
import { FabType, getfabProps, withFab } from 'src/components/higherOrder/withFab';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { updateAssortmentPlan } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.actions';
import { preSetActiveSubPage, setActiveSubPage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { WorklistViewDefn, zWorklistViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { formatViewDefnError, GroupViewItem } from 'configuration-schemas/viewdefns/general';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';

import { withRouter } from 'src/components/higherOrder/withRouter';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';
import { bindActionCreators } from '@reduxjs/toolkit';

export function mapStateToProps(state: AppState, ownProps: WorklistOwnProps): WorklistValueProps {
  const { title, hideTitle, defns, fabType = FabType.none, showFlowStatus } = ownProps;
  const fabProps = getfabProps(state, fabType);
  const viewState = worklistLens.get(state);
  const {
    isConfigLoading,
    selectedItemId,
    selectedLevel,
    worklistFlowStatus,
    worklistSearch,
    viewDataState,
    viewDefn,
  } = viewState;
  const subheader = subheaderLens.get(state);
  const adornments: AdornmentType[] = get(viewDefn, 'adornments', []);
  const { filteredStyles, treeStyles } = getProcessedData(state);

  // If the current worklist has `hideCompanion: true`, then we won't load the companion view and won't have `filteredStyles`.
  // So in that case just set `isLoading` to `isConfigLoading` and move on
  let isLoading = true;
  if (ownProps.hideCompanion) {
    isLoading = isConfigLoading;
  } else {
    isLoading =
      isConfigLoading || !isDataLoaded(viewDataState) || (filteredStyles?.length > 0 && selectedItemId == null);
  }

  return {
    title,
    hideTitle,
    config: viewDefn,
    styles: filteredStyles,
    treeStyles,
    subheaderSlice: subheader,
    subheaderViewDefns: defns.subheader,
    isLoading,
    viewDataState,
    showLevel: ownProps.showLevel,
    selectedLevel,
    selectedItemId,
    flowStatus: worklistFlowStatus,
    flowStatusConfig: subheader.flowStatusConfig,
    showFlowStatus: showFlowStatus == true ? true : false,
    companionSearch: worklistSearch,
    activeSubPage: state.perspective.activeSubPage,
    adornments,
    fab: fabProps,
    isPrintMode: state.print.isPrintMode,
    hideCompanion: ownProps.hideCompanion,
    isCompanionCollapsed: state.uiPose.isCompanionCollapsed,
  };
}

export function dispatchToProps(dispatch: AppThunkDispatch, ownProps: WorklistOwnProps): WorklistDispatchProps {
  const { defns } = ownProps;
  return {
    ...bindActionCreators({ onToggleCompanion: toggleCompanionCollapse }, dispatch),
    async onShowView() {
      // retrieve configs
      dispatch(requestWorklistConfig());

      try {
        const configs = await container.tenantConfigClient.getTenantViewDefns({
          defnIds: defns.view,
          appName: ASSORTMENT,
          // validationSchemas: [zWorklistViewDefn],
        });

        const [worklistConfig] = configs;
        if (worklistConfig) {
          dispatch(receiveWorklistConfig(worklistConfig as unknown as WorklistViewDefn));
        }
      } catch (error) {
        dispatch(
          receiveWorklistError({
            type: ComponentErrorType.config,
            message: formatViewDefnError(error),
            name: ConfDefnComponentType.worklist,
            issues: error,
          }),
        );
      }
    },
    updateSearchString(value: string) {
      dispatch(updateSearch(value));
    },
    updateCompanionSearchString(value: string) {
      dispatch(updateWorklistSearch(value));
    },
    async selectItem(item: string) {
      dispatch(updateSelectedItem(item));
    },
    selectLevel(level: GroupViewItem) {
      dispatch(updateSelectedLevel(level));
    },
    updateStyleItem(styleId: string, propToUpdate: string, propValue: string) {
      // FIXME: this should be changed to just refire the listData cache check and let the backend tell us about
      // the updates.
      const updated = worklistLens
        // TODO: we have to be smarter about this with caching coming in.
        .compose(Lens.fromPath<WorklistSlice>()(['liveData', 'flat']))
        .composeTraversal(fromTraversable(Traversable)())
        .composePrism(Prism.fromPredicate((item) => item.id === styleId))
        .modify((item: BasicPivotItem) => {
          return {
            ...item,
            [propToUpdate]: propValue,
          };
        });

      dispatch(update(updated, 'Edit Styles loaded'));
    },
    updateAssortmentPlan: () => {
      dispatch(updateAssortmentPlan('PlanQueue'));
    },
    setActiveSubPage(subPage: string) {
      dispatch(preSetActiveSubPage());
      dispatch(setActiveSubPage(subPage));
    },
    updateFlowStatus(values: number[]) {
      dispatch(updateWorklistFlowStatus(values));
    },
  };
}

export default connect(mapStateToProps, dispatchToProps)(makePrintSensitive(withRouter(withFab(Worklist))));
